/**
 * The phone sheet's two resting heights, and the drag between them.
 *
 * Half height keeps the page live above the chat, so you watch the page change
 * while the agent answers. Full height is for reading and typing. The sheet
 * follows your finger and settles on the nearest height, projected a little
 * along the flick; far enough down, it closes back into the corner.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type RefObject,
} from "react";

export type SheetDetent = "half" | "full";

/**
 * Where the half-height sheet's top edge rests, as a share of the screen: 392
 * of 844 (Figma 05 · Sheet · half height).
 */
export const SHEET_HALF_TOP = 392 / 844;
/** The full-height sheet's top edge, and its foot, from the screen's edges. */
const SHEET_FULL_TOP = 52;
const SHEET_FOOT = 8;
/** The sheet's visible move on spring.smooth, so the content can wait for it (see settled). */
const SHEET_MS = 380;
/** spring.throw's settle time (--spring-throw), after which a thrown sheet takes the open spring again. */
const THROW_MS = 680;
/** How far a release is carried along its flick, in ms of travel. */
const FLICK_MS = 180;
/** Under this many pixels a press on the grabber is a tap, not a drag. */
const TAP_SLOP = 4;

/** Where the sheet rests, as the value its transform reads. */
export function sheetOffset(open: boolean, detent: SheetDetent): string {
  if (!open) return "100%";
  return detent === "full" ? "0px" : HALF_OFFSET;
}

/** How far the half stop sits below the full one. */
const HALF_OFFSET = `max(0px, calc(${SHEET_HALF_TOP * 100}dvh - ${SHEET_FULL_TOP}px))`;

/**
 * The sheet's inline style: where it rests, and how much of it is below the
 * screen. At half height the content ends where the screen does, so the
 * composer sits at the visible bottom rather than off it.
 */
export function sheetStyle(open: boolean, detent: SheetDetent, settled: SheetDetent): CSSProperties {
  return {
    "--m7r-sheet-y": sheetOffset(open, detent),
    "--m7r-hidden": settled === "full" ? "0px" : HALF_OFFSET,
  } as CSSProperties;
}

export function useSheetDetents(sheetRef: RefObject<HTMLElement | null>, onClose: () => void) {
  const [detent, setDetent] = useState<SheetDetent>("half");
  // The content's height follows the sheet: at once when it grows to full,
  // and only after the slide when it drops to half, so nothing jumps up first.
  const [settled, setSettled] = useState<SheetDetent>("half");
  const dragging = useRef(false);
  // Ends a drag in progress without settling it, for unmount mid-drag.
  const abandonDrag = useRef<(() => void) | null>(null);
  const thrownTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const id = window.setTimeout(() => setSettled(detent), detent === "full" ? 0 : SHEET_MS);
    return () => window.clearTimeout(id);
  }, [detent]);

  useEffect(
    () => () => {
      abandonDrag.current?.();
      window.clearTimeout(thrownTimer.current);
    },
    [],
  );

  const onDragStart = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const sheet = sheetRef.current;
      if (!sheet || event.button !== 0 || dragging.current) return;
      const height = sheet.getBoundingClientRect().height;
      // The sheet's top at full height is the screen less its foot and itself.
      const fullTop = window.innerHeight - SHEET_FOOT - height;
      const half = Math.max(0, window.innerHeight * SHEET_HALF_TOP - fullTop);
      const start = detent === "full" ? 0 : half;
      const startY = event.clientY;
      let offset = start;
      let lastY = startY;
      let lastT = event.timeStamp;
      let velocity = 0;
      let moved = false;

      dragging.current = true;
      sheet.dataset.dragging = "true";
      window.clearTimeout(thrownTimer.current);
      delete sheet.dataset.thrown;

      const move = (e: globalThis.PointerEvent) => {
        const dy = e.clientY - startY;
        if (!moved && Math.abs(dy) < TAP_SLOP) return;
        moved = true;
        offset = Math.min(Math.max(start + dy, 0), height);
        const dt = e.timeStamp - lastT;
        if (dt > 0) velocity = (e.clientY - lastY) / dt;
        lastY = e.clientY;
        lastT = e.timeStamp;
        sheet.style.transform = `translate3d(0, ${offset}px, 0)`;
      };

      const release = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", end);
        window.removeEventListener("pointercancel", release);
        abandonDrag.current = null;
        dragging.current = false;
        delete sheet.dataset.dragging;
        sheet.style.transform = "";
      };

      const end = () => {
        release();
        // A tap on the grabber switches heights, as iOS does.
        if (!moved) {
          setDetent((current) => (current === "half" ? "full" : "half"));
          return;
        }
        // Let go after a drag, it settles on spring.throw rather than the open spring.
        sheet.dataset.thrown = "true";
        thrownTimer.current = window.setTimeout(() => delete sheet.dataset.thrown, THROW_MS);
        const projected = offset + velocity * FLICK_MS;
        const targets: Array<{ to: SheetDetent | "closed"; y: number }> = [
          { to: "full", y: 0 },
          { to: "half", y: half },
          { to: "closed", y: height },
        ];
        const nearest = targets.reduce((best, t) =>
          Math.abs(projected - t.y) < Math.abs(projected - best.y) ? t : best,
        );
        if (nearest.to === "closed") {
          setDetent("half");
          onClose();
        } else {
          setDetent(nearest.to);
        }
      };

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", end);
      // A cancelled touch (the system took it) goes back where it started.
      window.addEventListener("pointercancel", release);
      abandonDrag.current = release;
    },
    [detent, onClose, sheetRef],
  );

  return { detent, settled, setDetent, onDragStart };
}
